import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StatCardProps {
    title: string;
    value: React.ReactNode;
    description: string;
    subtitle?: React.ReactNode;
    className?: string;
    /** Percentage change from previous period. Positive = up, negative = down. */
    trend?: number | null;
    /** Override whether trend is considered positive (green) or negative (red). By default, positive trend = green. */
    trendInverted?: boolean;
}

export function StatCard({ title, value, description, subtitle, className, trend, trendInverted }: StatCardProps) {
    const hasTrend = trend !== null && trend !== undefined && isFinite(trend) && trend !== 0;
    const trendUp = hasTrend && trend > 0;
    const trendDown = hasTrend && trend < 0;
    // By default: up = green, down = red. If inverted: up = red, down = green.
    const trendPositive = trendInverted ? trendDown : trendUp;
    const trendNegative = trendInverted ? trendUp : trendDown;

    return (
        <Card className={cn("border-2 border-border rounded-2xl", className)}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger>
                            <Info className="h-4 w-4 text-muted-foreground transition-colors hover:text-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="max-w-xs text-xs">{description}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold">{value}</div>
                {hasTrend && (
                    <div className={cn(
                        "flex items-center gap-1 text-xs font-medium mt-1",
                        trendPositive && "text-green-600",
                        trendNegative && "text-red-500",
                        !trendPositive && !trendNegative && "text-muted-foreground"
                    )}>
                        {trendUp ? <TrendingUp className="h-3 w-3" /> : trendDown ? <TrendingDown className="h-3 w-3" /> : null}
                        {trendUp ? '+' : ''}{trend!.toFixed(2)}% vs prev period
                    </div>
                )}
                {!hasTrend && subtitle && (
                    <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
                )}
            </CardContent>
        </Card>
    );
}

// Currency display with smaller symbol - consistent styling across app
export const CurrencyDisplay = ({ amount, className = '' }: { amount: number; className?: string }) => {
    const absAmount = Math.abs(amount);
    const formattedValue = new Intl.NumberFormat('en', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(absAmount);

    return (
        <span className={className}>
            {formattedValue}
            <span className="text-sm font-normal">GHS</span>
        </span>
    );
};

// Percent display with smaller symbol - consistent styling across app
export const PercentDisplay = ({ value }: { value: number }) => (
    <>
        {value.toFixed(1)}
        <span className="text-sm font-normal">%</span>
    </>
);
